import { useMemo } from "react"

import { useAgentsListTransport } from "@/agents/transport"

import type { AgentRecord } from "@/types/api/agent"

type AgentsListData = AgentRecord[]
type AgentsListAllData = AgentRecord[]

interface LookupHookOptions {
  enabled?: boolean
  refetchOnMount?: "always" | boolean
}

interface UseAgentsListOptions<T> {
  enabled?: boolean
  refetchOnMount?: "always" | boolean
  select?: (data: AgentsListData) => T
  tagFilters?: string[]
}

export function useAgentsList<T = AgentsListData>(
  options?: UseAgentsListOptions<T>,
) {
  const { enabled, refetchOnMount, select, tagFilters } = options ?? {}

  const query = useAgentsListTransport({
    enabled,
    refetchOnMount,
    tagFilters,
  })

  const data = useMemo(
    () => (query.data ? (select ? select(query.data) : query.data) : undefined),
    [query.data, select],
  ) as T | undefined

  return useMemo(() => ({ ...query, data }), [data, query])
}

interface UseAgentsListAllOptions<T> {
  enabled?: boolean
  refetchOnMount?: "always" | boolean
  select?: (data: AgentsListAllData) => T
}

export function useAgentsListAll<T = AgentsListAllData>(
  options?: UseAgentsListAllOptions<T>,
) {
  const { enabled, refetchOnMount, select } = options ?? {}

  const query = useAgentsListTransport({
    includeInactive: true,
    enabled,
    refetchOnMount,
  })

  const data = useMemo(
    () => (query.data ? (select ? select(query.data) : query.data) : undefined),
    [query.data, select],
  ) as T | undefined

  return useMemo(() => ({ ...query, data }), [data, query])
}

export const useAgentsById = (options?: LookupHookOptions) => {
  const { data, isLoading, isError, isFetchedAfterMount, error, refetch } =
    useAgentsListAll({
      enabled: options?.enabled,
      refetchOnMount: options?.refetchOnMount,
    })

  const agentsById = useMemo(() => {
    return (data || []).reduce<Record<string, AgentRecord>>((acc, agent) => {
      if (agent) {
        acc[agent.id] = agent
      }
      return acc
    }, {})
  }, [data])

  return {
    agentsById,
    refetch,
    isLoading,
    isError,
    // False until the first fetch of this mount settles, so a caller pairing
    // this with refetchOnMount can wait for a revalidated lookup rather than
    // reading a cached one. A failed revalidation settles it too, which leaves
    // the caller on the cached agents instead of waiting forever, so a caller
    // that needs the lookup to be current pairs this with isError.
    isFetchedAfterMount,
    error,
  }
}
